import { inject, Injectable } from '@angular/core';
import { PlatformService } from 'ngx-unificator/services';
import { DOCUMENT } from '@angular/common';
import { WINDOW } from 'ngx-unificator/tokens';

declare var dataLayer: any[];

export const ActionsEnum = {
  LOGIN: 'login',
  REGISTER: 'register',
  CLICK: 'button_click',
} as const;

export type ActionType = typeof ActionsEnum[keyof typeof ActionsEnum];

export const CategoriesEnum = {
  FORM_SUBMIT: 'form_submit',
  DEPOSIT_START: 'deposit_start',
  LIMIT: 'limit',
  ABTEST: 'ab-test',
} as const;

export type CategoryType = typeof CategoriesEnum[keyof typeof CategoriesEnum];

export const TransactionsEnum = {
  DEPOSIT: 'deposit',
} as const;

export type TransactionType = typeof TransactionsEnum[keyof typeof TransactionsEnum];

export const GtagCookieEnum = {
  FIRST_DEPOSIT_SENT: 'gtm-first-deposit-sent',
  USER_IDS: 'gtm-user-ids',
} as const;

export type GtagCookieType = typeof GtagCookieEnum[keyof typeof GtagCookieEnum];


const TIMESTAMP = +new Date();

export interface GoogleTagManagerConfig {
  id: string | null;
  gtm_auth?: string;
  gtm_preview?: string;

  [key: string]: string | null | undefined;
}

@Injectable({
  providedIn: 'root',
})
export class GoogleTagManagerService {
  private _platform = inject(PlatformService);
  private _window = inject<Window>(WINDOW);
  private _document = inject<Document>(DOCUMENT);

  /**
   * List of already focused fields
   */
  private _firstFocusEventList = new Map<any, any>();

  private _isLoaded = false;
  public config: GoogleTagManagerConfig = { id: 'GTM-WRCGF3WP' };

  public getDataLayer(): any[] {
    const window = this._window;
    window['dataLayer'] = window['dataLayer'] || [];
    return window['dataLayer'];
  }

  public async addGtmToDom() {
    if (this._isLoaded || !this._platform.isBrowser) return;

    const doc: any = this._document;

    try {
      this._pushOnDataLayer({
        'gtm.start': new Date().getTime(),
        'event': 'gtm.js',
      });

      const gtmScript = doc.createElement('script');
      gtmScript.id = 'GTMscript';
      gtmScript.async = true;
      gtmScript.src = this._applyGtmQueryParams(
        this.config.gtm_resource_path || 'https://www.googletagmanager.com/gtm.js',
      );

      gtmScript.addEventListener('load', () => {
        this._isLoaded = true;
      });

      gtmScript.addEventListener('error', (e) => {
        console.warn('Failed to load:', e);
      });

      doc.head.insertBefore(gtmScript, doc.head.firstChild);

      const isPrivate = await this._isSafariPrivateMode();
      if (!isPrivate) {
        const iframe = doc.createElement('iframe');
        iframe.src = `https://www.googletagmanager.com/ns.html?id=${this.config.id}`;
        iframe.setAttribute('height', '0');
        iframe.setAttribute('width', '0');
        iframe.style.display = 'none';
        iframe.style.visibility = 'hidden';
        doc.body.appendChild(iframe);
      }
    } catch (err) {
      console.warn('Error initializing:', err);
    }
  }

  private _isSafariPrivateMode(): Promise<boolean> {
    return new Promise((resolve) => {
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      if (!isSafari) return resolve(false);

      const db = indexedDB.open('test');
      db.onerror = () => resolve(true);
      db.onsuccess = () => resolve(false);
    });
  }

  private _pushOnDataLayer(obj: object): void {
    const dataLayer = this.getDataLayer();
    dataLayer.push(obj);
  }

  private _applyGtmQueryParams(url: string): string {
    if (url.indexOf('?') === -1) {
      url += '?';
    }

    return (
      url +
      Object.keys(this.config)
        .filter(k => this.config[k])
        .map(k => `${k}=${this.config[k]}`)
        .join('&')
    );
  }

  /**
   * signUp Success
   * @userId
   */
  public signUpSuccess(userId) {
    this._pushOnDataLayer({
      event: 'create_account_button',
      event_action: ActionsEnum.CLICK,
      event_category: CategoriesEnum.FORM_SUBMIT,
      event_label: 'create_account',
      dimension1: userId,
    });
  }

  /**
   * Register form inputs focus
   * @param {string} eventName
   * @param {string} label
   */
  public registerFormInputFocus(eventName: string, label: string) {
    const eventVal = {
      event: eventName,
      eventAction: 'focus',
      eventCategory: 'register',
      eventLabel: label,
    };
    const eventKey = Object.values(eventVal).join();
    if (!this._firstFocusEventList.has(eventKey)) {
      this._firstFocusEventList.set(eventKey, true);
      this._pushOnDataLayer(eventVal);
    }
  }

  /**
   * Sign in success
   * @userId
   */
  public signInSuccess(userId) {
    this._pushOnDataLayer({
      event: 'login_account_button',
      event_action: ActionsEnum.CLICK,
      event_category: CategoriesEnum.FORM_SUBMIT,
      event_label: 'login_account',
      dimension1: userId,
    });
  }

  /**
   * Register error event
   * @param {string} eventName
   * @param error
   */
  public registerError(eventName: string, error) {
    this._pushOnDataLayer({
      event: eventName,
      event_action: 'register_error',
      event_category: CategoriesEnum.FORM_SUBMIT,
      event_label: error,
    });
  }

  /**
   * Login button on game page click
   * @param {string} eventName
   * @param {string} label
   */
  public setAbTest(eventName: string, action: string, label: string) {
    if (label) {
      this._pushOnDataLayer({
        event: eventName,
        event_action: action,
        event_category: CategoriesEnum.ABTEST,
        event_label: label,
      });
    }
  }

  /**
   * signUp Success Timestamp
   * @userId
   */
  public signUpSuccessTimestamp(eventName: string, label: string, userId: number) {
    this._pushOnDataLayer({
      event: eventName,
      event_action: TIMESTAMP,
      event_category: 'timestamp',
      event_label: label,
      dimension1: userId,
    });
  }

  /**
   * Game click event
   * @param gameid
   */
  public gameClickEvent(gameid: string) {
    this._pushOnDataLayer({
      event: 'game_click',
      gameid,
    });
  }

  /**
   * Banner Click Event
   * @param bannerOrder
   */
  public bannerClickEvent(bannerOrder: string) {
    this._pushOnDataLayer({
      event: 'click_on_main_banner',
      eventCategory: 'main_banner',
      eventLabel: 'event_label',
      eventValue: bannerOrder,
    });
  }

  /**
   * Popups event
   * @param modal
   */
  public popupEvent(popup: string) {
    this._pushOnDataLayer({
      event: 'Spinpandapopup',
      event_action: `open-${popup}`,
      event_category: popup,
    });
  }

  public depositSuccessEvent(transactionId: string, amount: number, currency: string) {
    this._pushOnDataLayer({
      event: 'purchase',
      id: transactionId,
      value: amount,
      currency: currency
    });
  }
}
